import React, {  useState } from 'react';
import { Modal } from 'antd';
import styled from 'styled-components';
import WizrText from '../WizrText';
import ConfluenceIcon from '../../assets/icons/confluence';
import SlackIcon from '../../assets/icons/SlackIcon';
import UrlIcon from '../../assets/icons/UrlIcon';
import WizrUploadContainer from '../WizrUploadButton';
import ComputerIcon from '../../assets/icons/Computer';
import GoogleDriveIcon from '../../assets/icons/GoogleDrive';
import OneDriveIcon from '../../assets/icons/oneDrive';
import DocAiIcon from '../../assets/icons/Doc.ai';
import { useContentFilesSelector } from '../../redux/selectors/content';
import { dispatchListFiles, dispatchUploadFileUsingLink } from '../../redux/actions/content';
import SharepointIcon from '../../assets/icons/sharepoint';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import useDrivePicker from 'react-google-drive-picker';
import axios from 'axios';
import { uploadCheck } from '../../utils';
import { dispatchNotificationState } from '../../redux/actions/common';

const ModalContainer = styled.div`
  padding: 20px;
`;

const SourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 268px);
  gap: 16px;
  margin-bottom: 16px;
  
  > * {
    min-width: 0;
  }
`;

const SourceCard = styled.div<{ bordered?: boolean; selected?: boolean }>`
  position: relative;
  width: 268px;
  height: 120px;
  background: #EAECF1;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  box-sizing: border-box;
  border: ${props => props.selected ? '2px solid #000' : 'none'};
  align-items: flex-start;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DescriptionText = styled.div`
  color: #666;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'Montserrat';
`;

const TagContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  margin-bottom: auto;
`;

const Tag = styled.span`
  background: #DCFCE7;
  color: #1D1F22;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  border: 1px solid #4ADE80;
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const InfoText = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 12px;
`;

const ContinueButton = styled.button`
  width: auto;
  padding: 8px 24px;
  background: #000;
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;

  &:hover {
    background: #333;
  }
`;

const ComingSoonBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #EAECF1;
  color:#999B9F;
  padding: 4px 8px;
  border-radius: 12px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-style: italic;
  font-size: 10px;
  line-height: 10px;
  letter-spacing: 0px;
  text-align: right;
  
`;
interface SharePointFile {
    id: string;
    name: string;
    webUrl: string;
    size: number;
    lastModified: string;
  }
  
  interface SharePointSite {
    id: string;
    name: string;
    webUrl: string;
  }
  
  // Update the component props to include folderId and testFolderId
  interface SharePointPickerProps {
    folderId?: string;
    testFolderId?: string;
  }
interface AddSourceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onFileUpload: (fileInfo: any, docAI?: boolean) => void;
    onDocAIUpload: (fileInfo: any, docAI?: boolean) => void;
    onUrlUpload: () => void;
    onConfluenceUpload: () => void;
    onSlackUpload: () => void;
    folderId: string;
    testFolderId: string;
    onSharePointUpload: (fileType: any) => void;
    onOneDriveUpload: () => void;
    seismicUpload: () => void;
}

// Define the type outside
interface SourceCardData {
  id: string;
  title: string;
  icon: React.FC<{ width?: string; height?: string }>;
  tags: string[];
  action?: (fileInfo?: any) => void;
  comingSoon?: boolean;
  description?: string;
}

// Create a separate component for Google Drive functionality
const GoogleDriveButton: React.FC<{ onClose: () => void, folderId: string, testFolderId: string }> = ({ onClose, folderId, testFolderId }) => {
    const CLIENT_ID = process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID as string;
    const API_KEY = process.env.REACT_APP_GOOGLE_DRIVE_API_KEY as string;
    const [openPicker] = useDrivePicker();
    const [uploadedGoogleDriveFileNames, setUploadedGoogleDriveFileNames] = useState<string[]>([]);
    const existingFilesData = useContentFilesSelector(); // Get existing files

    const checkIfDuplicateFileExists = (fileName: string, existingFiles: any[]) => {
        const existingFileNamesSet = new Set(existingFiles.map((existingItem) => existingItem.file_name.toLowerCase()));
        return existingFileNamesSet.has(fileName.toLowerCase());
    };

    const getExportMimeType = (mimeType: string): string => {
        switch (mimeType) {
            case 'application/vnd.google-apps.document':
                return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'; // DOCX
            case 'application/vnd.google-apps.spreadsheet':
                return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'; // XLSX
            case 'application/vnd.google-apps.presentation':
                return 'application/vnd.openxmlformats-officedocument.presentationml.presentation'; // PPTX
            default:
                return '';
        }
    };

    const getFileExtension = (mimeType: string): string => {
        if (mimeType.includes('application/pdf')) return '.pdf';
        if (mimeType.includes('application/vnd.ms-excel') || mimeType.includes('text/csv')) return '.csv';
        if (mimeType.includes('application/vnd.google-apps.document')) return '.docx';
        if (mimeType.includes('application/vnd.google-apps.spreadsheet')) return '.xlsx';
        if (mimeType.includes('application/vnd.openxmlformats-officedocument.presentationml.presentation')) return '.pptx';
        if (mimeType.includes('text/plain')) return '.txt';
        return '';
    };

    const addFileExtension = (doc: any, mimeType: string) => {
        const knownExtensions = ['.pdf', '.csv', '.docx', '.xlsx', '.pptx', '.txt'];
        const hasKnownExtension = knownExtensions.some(ext => doc.name.toLowerCase().endsWith(ext));

        if (!hasKnownExtension) {
            const extension = getFileExtension(mimeType);
            if (extension) {
                doc.name += extension;
            }
        }
    };

    const handleFileUpload = async (data: any, tokenResponse: any) => {
        const { id: fileId, mimeType, sizeBytes: fileSize, name: fileName } = data.docs[0];

        if (checkIfDuplicateFileExists(fileName, existingFilesData)) {
            dispatchNotificationState({
                message: 'Warning',
                description: `Duplicate file name "${fileName}" detected. This file has already been uploaded from Google Drive.`,
                type: 'warning',
            });
            return;
        }

        const isValid = uploadCheck(8, { size: fileSize, type: mimeType });
        if (!isValid) {
            return;
        }

        let response: any;
        if (mimeType.includes('application/vnd.google-apps')) {
            const exportMimeType = getExportMimeType(mimeType);
            response = await axios.get(
                `https://www.googleapis.com/drive/v3/files/${fileId}/export`,
                {
                    params: { mimeType: exportMimeType },
                    headers: { 'Authorization': `Bearer ${tokenResponse.access_token}` },
                    responseType: 'blob'
                }
            );
        } else {
            response = await axios.get(
                `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
                {
                    headers: { 'Authorization': `Bearer ${tokenResponse.access_token}` },
                    responseType: 'blob'
                }
            );
        }

        setUploadedGoogleDriveFileNames([...uploadedGoogleDriveFileNames, fileName]);
        const timestamp = new Date().getTime();

        data.docs.forEach((doc: any) => {
            doc.content = response.data;
            addFileExtension(doc, mimeType);
        });

        // Upload each selected Google Drive doc via API using FormData
        for (const doc of data.docs) {
            const fileName = doc.name;
            const fileUploadPayload = {
                container_id: 3,
                path: `${folderId || testFolderId}/${timestamp}`,
                timestamp: timestamp,
                files: [fileName],
                tags: [],
                content_type: 'googledrive'
            };
            const formData = new FormData();
            const jsonData = JSON.stringify(fileUploadPayload);
            formData.append('data', jsonData);
            // doc.content is a Blob; pass filename to preserve name on server
            formData.append(fileName, doc.content, fileName);
            await dispatchUploadFileUsingLink(formData);
        }

        if (folderId) {
            setTimeout(() => {
                dispatchListFiles(folderId);
            }, 3000);
        }

        onClose();
    };

    const handleGoogleDrive = useGoogleLogin({
        onSuccess: (tokenResponse) => {
            if (tokenResponse.access_token) {
                openPicker({
                    clientId: CLIENT_ID ,
                    developerKey: API_KEY ,
                    viewId: "DOCS",
                    token: tokenResponse.access_token,
                    showUploadView: true,
                    showUploadFolders: true,
                    supportDrives: true,
                    multiselect: true,
                    callbackFunction: (data: any) => {
                         console.log('data.docsfirst', data.docs);
                         
                         if (data.action === 'picked') {
                             handleFileUpload(data, tokenResponse).catch((error: any) => {
                                 console.error('Error processing file:', error.message);
                             });
                         }
                     }
                });
            }
        },
        onError: () => console.error("Google Login Failed"),
        scope: "https://www.googleapis.com/auth/drive.readonly",
        flow: "implicit",
    });

    return (
        <SourceCard onClick={() => handleGoogleDrive()}>
            <TitleContainer>
                <div style={{ width: '27px', height: '24px' }}>
                    <GoogleDriveIcon width="100%" height="100%" />
                </div>
                <WizrText type="body1">Google Drive</WizrText>
            </TitleContainer>
            <DescriptionText>
                Connect your Google Drive account to browse and upload files.
            </DescriptionText>
            <TagContainer>
                {['PDF', 'Word', 'DOC'].map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                ))}
            </TagContainer>
        </SourceCard>
    );
};

// Create a separate component for SharePoint functionality


const AddSourceModal: React.FC<AddSourceModalProps> = ({
    isOpen,
    onClose,
    onFileUpload,
    onDocAIUpload,
    onUrlUpload,
    onConfluenceUpload,
    onSlackUpload,
    folderId,
    testFolderId,
    onSharePointUpload,
    onOneDriveUpload,
    seismicUpload
}) => {
    const handleCardClick = (card: SourceCardData) => {
        if (card.action) {
            card.action();
            onClose();
        }
    };
    const sourceCards: SourceCardData[] = [
        {
            id: 'computer',
            title: 'My computer',
            icon: ComputerIcon,
            description: 'Directly upload files from your local storage for quick access.',
            tags: ['PDF', 'Word', 'DOC', 'CSV', 'PPTX'],
            action: (fileInfo: any) => {
                
                onFileUpload(fileInfo, false);
            }
        },
        {
            id: 'webpage',
            title: 'Web Pages',
            icon: UrlIcon,
            description: 'Provide a URL to retrieve files from online sources effortlessly.',
            tags: ['Web URLs'],
            action: onUrlUpload
        },
        {
            id: 'confluence',
            title: 'Confluence',
            icon: ConfluenceIcon,
            description: 'Integrate with Confluence and upload documents using shared links.',
            tags: ['Space URLs'],
            action: onConfluenceUpload
        },
        {
            id: 'slack',
            title: 'Slack',
            icon: SlackIcon,
            description: 'Upload documents from Slack channels where files have been shared.',
            tags: ['Channels'],
            action: onSlackUpload
        },
        {
            id: 'docai',
            title: 'Using DOC.ai',
            icon: DocAiIcon,
            description: 'DOC.ai is used for uploading the pdf with images and tables',
            tags: ['PDF with images & table'],
            action: (fileInfo: any) => {
                console.log('docAI inside inside fileupload', fileInfo)
                onDocAIUpload(fileInfo, true);
            }
        },

        {
            id: 'sharepointpages',
            title: 'Share Point Pages',
            icon: SharepointIcon,
            description: 'Access and upload pages stored in your SharePoint sites.',
            tags: ['Sharepoint pages'],
            action: () => onSharePointUpload('pages')
        },
        {
            id: 'sharepointdocuments',
            title: 'Share Point Docs',
            icon: SharepointIcon,
            description: 'Access and upload documents stored in your SharePoint sites.',
            tags: ['Sharepoint documents'],
            action: () => onSharePointUpload('documents')
        },
        {
            id: 'onedrive',
            title: 'One Drive',
            icon: OneDriveIcon,
            description: 'Access and upload documents stored in your One Drive.',
            tags: ['Onedrive documents'],
            action: () => onOneDriveUpload()
        },
        {
            id: 'seismic',
            title: 'Seismic',
            icon: OneDriveIcon,
            description: 'Access and upload documents stored in seismic',
            tags: ['Seismic documents'],
            action: () => seismicUpload()
        },
    ];
 
   const CLIENT_ID = process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID as string;
    return (
        <Modal
            title="Add Sources"
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={1300}
            height={286}
            style={{
                gap: 13,
            }}
        >   
            <ModalContainer>
                <SourcesGrid>
                    {sourceCards.map((card) => {
                        if (card.id === 'computer' || card.id === 'docai') {
                            return (
                                <WizrUploadContainer
                                    key={card.id}
                                    onChange={card.action}
                                >
                                    <SourceCard>
                                        <TitleContainer>
                                            <div style={{ width: '27px', height: '24px' }}>
                                                <card.icon width="100%" height="100%" />
                                            </div>
                                            <WizrText type="body1">{card.title}</WizrText>
                                        </TitleContainer>
                                        {card.description && (
                                            <DescriptionText>{card.description}</DescriptionText>
                                        )}
                                        <TagContainer>
                                            {card.tags.map((tag) => (
                                                <Tag key={tag}>{tag}</Tag>
                                            ))}
                                        </TagContainer>
                                        {card.comingSoon && (
                                            <ComingSoonBadge>Coming Soon</ComingSoonBadge>
                                        )}
                                    </SourceCard>
                                </WizrUploadContainer>
                            );
                        }

                        return (
                            <SourceCard
                                key={card.id}
                                onClick={() => handleCardClick(card)}
                            >
                                <TitleContainer>
                                    <div style={{ width: '27px', height: '24px' }}>
                                        <card.icon width="100%" height="100%" />
                                    </div>
                                    <WizrText type="body1">{card.title}</WizrText>
                                </TitleContainer>
                                {card.description && (
                                    <DescriptionText  >{card.description}</DescriptionText>
                                )}
                                <TagContainer>
                                    {card.tags.map((tag) => (
                                        <Tag key={tag}>{tag}</Tag>
                                    ))}
                                </TagContainer>
                                {card.comingSoon && (
                                    <ComingSoonBadge>Coming Soon</ComingSoonBadge>
                                )}
                            </SourceCard>
                        );
                    })}
                    <GoogleOAuthProvider clientId={CLIENT_ID}>
                        <GoogleDriveButton onClose={onClose} folderId={folderId} testFolderId={testFolderId} />
                    </GoogleOAuthProvider>
                   
                </SourcesGrid>

                <FooterContainer>
                   
                </FooterContainer>
            </ModalContainer>
        </Modal>
    );
};

export default AddSourceModal; 